import React from 'react'
import type { Block } from '../../editor/types'

export type ImageFrame = 'none' | 'browser' | 'iphone' | 'laptop' | 'ipad' | 'android' | 'polaroid'

interface ImageBlockProps {
  block: Block
}

function getConfig(block: Block) {
  const config = (block.config || {}) as Record<string, unknown>
  return {
    url: (config.url as string) || '',
    frame: (config.frame as ImageFrame) || 'none',
    alt: (config.alt as string) || block.label || 'Image',
  }
}

export default function ImageBlock({ block }: ImageBlockProps) {
  const { url, frame, alt } = getConfig(block)

  if (!url) {
    return (
      <div className="h-40 bg-canvas rounded-lg flex items-center justify-center text-text-secondary text-sm">
        No image URL set
      </div>
    )
  }

  switch (frame) {
    case 'browser':
      return <BrowserFrame url={url} alt={alt} />
    case 'iphone':
      return <IPhoneFrame url={url} alt={alt} />
    case 'laptop':
      return <LaptopFrame url={url} alt={alt} />
    case 'ipad':
      return <IPadFrame url={url} alt={alt} />
    case 'android':
      return <AndroidFrame url={url} alt={alt} />
    case 'polaroid':
      return <PolaroidFrame url={url} alt={alt} />
    default:
      return <img src={url} alt={alt} className="w-full rounded-lg" />
  }
}

/* ========== Browser Frame ========== */
function BrowserFrame({ url, alt }: { url: string; alt: string }) {
  return (
    <div className="rounded-xl overflow-hidden shadow-2xl border border-slate-200">
      {/* Browser chrome */}
      <div className="bg-slate-100 px-4 py-2.5 flex items-center gap-3 border-b border-slate-200">
        <div className="flex gap-1.5">
          <div className="size-3 rounded-full bg-red-400" />
          <div className="size-3 rounded-full bg-amber-400" />
          <div className="size-3 rounded-full bg-green-400" />
        </div>
        <div className="flex-1 bg-white rounded-md px-3 py-1 text-[11px] text-slate-400 font-mono truncate border border-slate-200">
          {url.length > 50 ? url.slice(0, 50) + '...' : url}
        </div>
      </div>
      <img src={url} alt={alt} className="w-full block" />
    </div>
  )
}

/* ========== iPhone Frame ========== */
function IPhoneFrame({ url, alt }: { url: string; alt: string }) {
  return (
    <div className="flex justify-center">
      <div className="relative w-[280px]">
        {/* Phone shell */}
        <div className="bg-[#1a1a1a] rounded-[3rem] p-[10px] shadow-2xl ring-1 ring-slate-700/50">
          {/* Dynamic Island */}
          <div className="absolute top-[18px] left-1/2 -translate-x-1/2 w-[90px] h-[24px] bg-[#1a1a1a] rounded-full z-10" />
          {/* Screen */}
          <div className="bg-white rounded-[2.25rem] overflow-hidden">
            {/* Status bar */}
            <div className="h-14 bg-white flex items-end justify-between px-8 pb-1">
              <span className="text-[10px] font-semibold text-slate-900">9:41</span>
              <div className="flex gap-1 items-center">
                <svg width="16" height="10" viewBox="0 0 16 10" className="text-slate-900">
                  <rect x="0" y="3" width="3" height="7" rx="0.5" fill="currentColor" />
                  <rect x="4.5" y="2" width="3" height="8" rx="0.5" fill="currentColor" />
                  <rect x="9" y="0.5" width="3" height="9.5" rx="0.5" fill="currentColor" />
                  <rect x="13" y="0" width="3" height="10" rx="0.5" fill="currentColor" opacity="0.3" />
                </svg>
                <div className="w-[22px] h-[10px] border border-slate-900 rounded-[3px] relative ml-0.5">
                  <div className="absolute top-[1.5px] left-[1.5px] bottom-[1.5px] bg-slate-900 rounded-[1.5px]" style={{ width: '65%' }} />
                  <div className="absolute -right-[3px] top-[2.5px] w-[1.5px] h-[5px] bg-slate-900 rounded-r-sm" />
                </div>
              </div>
            </div>
            {/* Screen content — fixed height, scrollable look */}
            <div className="h-[480px] overflow-hidden">
              <img src={url} alt={alt} className="w-full h-full object-cover" />
            </div>
            {/* Home indicator */}
            <div className="h-8 bg-white flex items-center justify-center">
              <div className="w-[100px] h-[4px] bg-slate-900 rounded-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ========== Laptop Frame (SVG overlay approach) ========== */
function LaptopFrame({ url, alt }: { url: string; alt: string }) {
  const screen = {
    left: (168 / 1200) * 100,
    top: (42 / 780) * 100,
    width: (864 / 1200) * 100,
    height: (600 / 780) * 100,
  }

  return (
    <div className="flex justify-center">
      <div className="w-full max-w-[600px] relative">
        <div
          className="absolute overflow-hidden rounded-[0.3%]"
          style={{
            left: `${screen.left}%`,
            top: `${screen.top}%`,
            width: `${screen.width}%`,
            height: `${screen.height}%`,
          }}
        >
          <img src={url} alt={alt} className="w-full h-full object-cover" />
        </div>
        <img src="/frames/laptop.svg" alt="" className="w-full block relative z-10 pointer-events-none" />
      </div>
    </div>
  )
}

/* ========== iPad Frame ========== */
function IPadFrame({ url, alt }: { url: string; alt: string }) {
  return (
    <div className="flex justify-center">
      <div className="relative w-[420px]">
        <div className="bg-[#1a1a1a] rounded-[2rem] p-[12px] shadow-2xl ring-1 ring-slate-700/50">
          {/* Camera */}
          <div className="absolute top-[20px] left-1/2 -translate-x-1/2 w-[8px] h-[8px] bg-[#2a2a2a] rounded-full z-10 ring-1 ring-slate-600/30" />
          {/* Screen */}
          <div className="bg-white rounded-[1.25rem] overflow-hidden">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={url} alt={alt} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ========== Android Frame ========== */
function AndroidFrame({ url, alt }: { url: string; alt: string }) {
  return (
    <div className="flex justify-center">
      <div className="relative w-[280px]">
        <div className="bg-[#1a1a1a] rounded-[2rem] p-[8px] shadow-2xl ring-1 ring-slate-700/50">
          {/* Punch-hole camera */}
          <div className="absolute top-[16px] left-1/2 -translate-x-1/2 w-[10px] h-[10px] bg-[#0a0a0a] rounded-full z-10 ring-1 ring-slate-600/40" />
          {/* Screen */}
          <div className="bg-white rounded-[1.5rem] overflow-hidden">
            <div className="h-10" />
            <div className="h-[520px] overflow-hidden">
              <img src={url} alt={alt} className="w-full h-full object-cover" />
            </div>
            {/* Nav bar */}
            <div className="h-8 bg-white flex items-center justify-center gap-6">
              <div className="w-[14px] h-[14px] border-2 border-slate-300 rounded-sm" />
              <div className="w-[14px] h-[14px] border-2 border-slate-300 rounded-full" />
              <div className="w-0 h-0 border-l-[8px] border-l-slate-300 border-y-[7px] border-y-transparent" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ========== Polaroid Frame ========== */
function PolaroidFrame({ url, alt }: { url: string; alt: string }) {
  return (
    <div className="flex justify-center">
      <div className="bg-white p-4 pt-4 pb-14 shadow-xl rotate-[-2deg] hover:rotate-0 transition-transform duration-300" style={{ maxWidth: 400 }}>
        <img src={url} alt={alt} className="w-full aspect-square object-cover" />
      </div>
    </div>
  )
}
